import { Observable, timer } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { SettingBaseService } from './setting-base.service';
import { Response } from '../domain/response/response';
import { shareReplay } from 'rxjs/operators';
import { SettingEntity } from '../domain/setting.entity';
import { PasswordDTO } from './password-dto';
import { SettingCriteria } from './setting.criteria';

import { User } from '../authentication/service/user';
import { AuthenticationService } from '../authentication/service/authentication.service';
/** Refresh interval set to one minute (in milliseconds). */
const REFRESH_INTERVAL = 1000 * 60;
const ADMIN_ROLE = 'System Admin';

export interface Settings<T = string | number> {
  [setting: string]: T;
}

@Injectable()
export class SettingService extends SettingBaseService {
  private authenticationService = inject(AuthenticationService);

  private cache$: Observable<Settings>;
  private currentUser: User;

  public settingCriteria: SettingCriteria;

  constructor() {
    const http = inject(HttpClient);

    super(http);
    // we just simply clear the cache every refresh interval and let it be refreshed by the next getAll call
    timer(0, REFRESH_INTERVAL).subscribe(() => (this.cache$ = null));
    this.authenticationService.currentUser$.subscribe(
      (currentUser) => (this.currentUser = currentUser)
    );
  }

  public save(entity: SettingEntity): Observable<Response<SettingEntity>> {
    // clear cache so next getAll returns most recent values
    this.cache$ = null;
    return super.save(entity);
  }

  public delete(docId: string) {
    // clear cache so next getAll returns most recent values
    this.cache$ = null;
    return super.delete(docId);
  }

  public get<T = string | number>(ids: string[]) {
    // const params = new HttpParams();
    // ids.forEach((id) => params.append('id', id));
    return this.http.get<Settings<T>>('/api/setting', { params: { id: ids } });
  }

  public put(settings: Settings) {
    // clear cache so next getAll returns most recent values
    this.cache$ = null;
    return this.http.put<Response<Settings>>('/api/setting', settings);
  }

  public getAll() {
    // using first method from blog post https://blog.thoughtram.io/angular/2018/03/05/advanced-caching-with-rxjs.html
    // so that we only go and get the defaults once
    if (!this.cache$) {
      this.cache$ = this.http.get<Settings>('/api/setting/all').pipe(shareReplay(1));
    }
    return this.cache$;
  }

  public getPassword(passwordType: string): Observable<PasswordDTO> {
    return this.http.get<PasswordDTO>('/api/setting/password/' + passwordType);
  }

  public getHelpLink() {
    return this.http.get('/api/setting/helpLink', { responseType: 'text' });
  }

  public isAuthorizedToAdd(): boolean {
    const roles = this.currentUser.authorities;
    for (const role of roles) {
      if (role.toLowerCase() === ADMIN_ROLE.toLowerCase()) {
        return true;
      }
    }
    return false;
  }
}
